/**
 * Sample Availability Manager
 *
 * Gates products flagged with `custom.opening_order_sample = true`:
 *   - Block purchase entirely when the customer has already placed an order
 *     with this product's brand (vendor).
 *   - Otherwise cap the per-order quantity at
 *     `custom.max_quantity_per_customer` (defaults to 1).
 *
 * The "has purchased from brand?" signal reuses the purchased-brands worker
 * already powering opening-order minimum detection.
 *
 * Affected DOM markers (set by Liquid in buy-buttons.liquid + card-product.liquid):
 *   [data-opening-order-sample="true"]
 *     data-opening-order-sample-vendor="<vendor name>"
 *     data-max-qty-per-customer="<integer>"
 *
 * Guest and non-B2B customers see no restrictions (fails open). Rationale: the
 * Worker signal relies on a customer ID; without one we don't know and we err
 * on the side of letting the sale happen.
 *
 * Config (optional) on window.sampleAvailabilityConfig:
 *   customerId: number|null
 *   workerUrl: string
 *   blockedLabel: string       // button text when blocked
 *   blockedMessage: string     // message shown to customer when blocked
 */
(function (global) {
  'use strict';

  class SampleAvailabilityManager {
    constructor() {
      this.config = Object.assign(
        {
          blockedLabel: 'Not available on repeat orders',
          blockedMessage: 'Samples are only available on your first order with this brand.',
        },
        global.sampleAvailabilityConfig || {}
      );
      this.purchasedBrands = new Set();
      this.loaded = false;
      this.loadPromise = null;
    }

    async init() {
      if (!this.config.customerId || !this.config.workerUrl) {
        this.loaded = true;
        this.applyToAll();
        return;
      }

      this.loadPromise = fetch(
        `${this.config.workerUrl}/customer/purchased-brands?customerId=${this.config.customerId}`,
        { credentials: 'same-origin' }
      )
        .then((r) => (r.ok ? r.json() : { success: false }))
        .then((data) => {
          if (data.success && Array.isArray(data.purchasedBrands)) {
            data.purchasedBrands.forEach((b) => {
              if (b && b.vendor) this.purchasedBrands.add(b.vendor);
            });
          }
        })
        .catch((err) => console.warn('[SampleAvailability] Worker fetch failed:', err))
        .finally(() => {
          this.loaded = true;
          this.applyToAll();
        });

      document.addEventListener('cart:updated', () => this.applyToAll());
      // Re-apply when DOM changes (new product cards rendered dynamically).
      this.observeDom();
    }

    isBlocked(vendor) {
      if (!vendor) return false;
      return this.purchasedBrands.has(vendor);
    }

    applyToAll() {
      if (!this.loaded) return;
      document.querySelectorAll('[data-opening-order-sample="true"]').forEach((el) => this.apply(el));
    }

    apply(el) {
      const vendor = el.dataset.openingOrderSampleVendor || '';
      const maxQty = Math.max(1, parseInt(el.dataset.maxQtyPerCustomer || '1', 10));

      if (this.isBlocked(vendor)) {
        this.block(el);
      } else {
        this.capQuantity(el, maxQty);
      }
    }

    /**
     * Return every quantity input associated with a product-form element,
     * both by DOM containment and by the `form="<id>"` attribute pattern
     * (used by sections/main-product.liquid to keep the qty selector outside
     * the <product-form> wrapper).
     */
    findQtyInputs(el) {
      const inputs = new Set();
      el.querySelectorAll('input[type="number"], input[name="quantity"]').forEach((i) => inputs.add(i));
      // HTML <form> elements expose named children as properties, so `form.id`
      // can be shadowed by <input name="id"> (common in Shopify product forms).
      // Use getAttribute to read the actual id attribute.
      const innerForm = el.querySelector('form');
      const formId = innerForm && innerForm.getAttribute('id');
      if (formId) {
        document.querySelectorAll(`input[form="${CSS.escape(formId)}"]`).forEach((i) => inputs.add(i));
      }
      return [...inputs];
    }

    block(el) {
      if (el.dataset.sampleGateApplied === 'blocked') return;
      el.dataset.sampleGateApplied = 'blocked';
      el.classList.add('opening-order-sample--blocked');

      const submit = el.querySelector('button[type="submit"][name="add"], button[name="add"], [data-add-to-cart]');
      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-disabled', 'true');
        const span = submit.querySelector('span') || submit;
        if (!submit.dataset.originalLabel) submit.dataset.originalLabel = span.textContent.trim();
        span.textContent = this.config.blockedLabel;
      }

      const msg = el.querySelector('[data-opening-sample-message]');
      if (msg) {
        msg.classList.remove('hidden');
        const text = msg.querySelector('span');
        if (text) text.textContent = this.config.blockedMessage;
      }

      this.findQtyInputs(el).forEach((input) => {
        input.value = 0;
        input.disabled = true;
      });
    }

    capQuantity(el, maxQty) {
      if (el.dataset.sampleGateApplied === 'capped' && el.dataset.sampleGateCap === String(maxQty)) return;
      el.dataset.sampleGateApplied = 'capped';
      el.dataset.sampleGateCap = String(maxQty);

      this.findQtyInputs(el).forEach((input) => {
        input.max = maxQty;
        const current = parseInt(input.value || '1', 10);
        if (current > maxQty) input.value = maxQty;
      });
    }

    observeDom() {
      if (!('MutationObserver' in window)) return;
      const observer = new MutationObserver(() => {
        // Debounce: apply on next tick.
        clearTimeout(this._applyTimer);
        this._applyTimer = setTimeout(() => this.applyToAll(), 50);
      });
      observer.observe(document.body, { childList: true, subtree: true });
      this._observer = observer;
    }
  }

  global.SampleAvailabilityManager = SampleAvailabilityManager;

  document.addEventListener('DOMContentLoaded', () => {
    if (!global.__sampleAvailabilityManager) {
      global.__sampleAvailabilityManager = new SampleAvailabilityManager();
      global.__sampleAvailabilityManager.init();
    }
  });
})(window);
